// Suggestion buttons on the mapping page used to GET every unmapped row on the
// page after each map, unmap, and create. This cache keeps a result per entity
// id, skips rows that already have one, and shares an in-flight GET so a second
// load does not start the same call again.

export interface SuggestionRowRef {
    id: number;
    arenaEntityId?: number | null;
    hasSuggestion?: boolean;
    entityType?: number;
    parentSourceId?: number | null;
}

export interface SuggestionCacheEntry<T> {
    value: T;
    // False when the search flag already said there is no hit, so no GET ran.
    fromNetwork: boolean;
    entityType?: number;
    parentSourceId?: number | null;
}

export interface SuggestionPlan {
    id: number;
    action: 'fetch' | 'seed-empty';
}

export function planSuggestionLoads<T>(
    rows: readonly SuggestionRowRef[],
    cache: ReadonlyMap<number, SuggestionCacheEntry<T>>,
    inFlight: ReadonlySet<number>
): SuggestionPlan[] {
    const planned: SuggestionPlan[] = [];
    const seen = new Set<number>();
    for (const row of rows) {
        if (seen.has(row.id)) {
            continue;
        }
        seen.add(row.id);
        if (row.arenaEntityId) {
            continue;
        }
        if (inFlight.has(row.id)) {
            continue;
        }
        const entry = cache.get(row.id);
        if (!entry) {
            // Search uses the same score as GET suggestions. A false flag is an
            // empty list, so the button column stays "-" without a request.
            planned.push({
                id: row.id,
                action: row.hasSuggestion === false ? 'seed-empty' : 'fetch',
            });
            continue;
        }
        // A later search can flag a hit after we stored an empty list locally
        // (a create added an Arena row that now matches). Fetch that row only.
        if (!entry.fromNetwork && row.hasSuggestion === true) {
            planned.push({ id: row.id, action: 'fetch' });
        }
    }
    return planned;
}

interface InFlightMeta<T> {
    promise: Promise<T>;
    entityType?: number;
    parentSourceId?: number | null;
}

export class SuggestionCache<T> {
    private readonly entries = new Map<number, SuggestionCacheEntry<T>>();
    private readonly inflight = new Map<number, Promise<T>>();
    private readonly inflightMeta = new Map<number, InFlightMeta<T>>();
    private readonly generation = new Map<number, number>();

    public planFor(rows: readonly SuggestionRowRef[]): SuggestionPlan[] {
        return planSuggestionLoads(rows, this.entries, new Set(this.inflight.keys()));
    }

    public seed(id: number, value: T, row?: SuggestionRowRef): void {
        if (this.entries.has(id) || this.inflight.has(id)) {
            return;
        }
        this.entries.set(id, this.entryFrom(value, false, row));
    }

    public invalidate(ids: readonly number[]): void {
        for (const id of ids) {
            this.bump(id);
            this.entries.delete(id);
            this.inflight.delete(id);
            this.inflightMeta.delete(id);
        }
    }

    public invalidateAll(): void {
        Array.from(this.entries.keys()).forEach((id: number): void => {
            this.bump(id);
        });
        Array.from(this.inflight.keys()).forEach((id: number): void => {
            this.bump(id);
        });
        this.entries.clear();
        this.inflight.clear();
        this.inflightMeta.clear();
    }

    // Team suggestions are limited to the parent club once that club is mapped.
    public invalidateChildren(entityType: number, parentSourceId: number): void {
        const ids = new Set<number>();
        this.entries.forEach((entry, id): void => {
            if (entry.entityType === entityType && entry.parentSourceId === parentSourceId) {
                ids.add(id);
            }
        });
        this.inflightMeta.forEach((meta, id): void => {
            if (meta.entityType === entityType && meta.parentSourceId === parentSourceId) {
                ids.add(id);
            }
        });
        this.invalidate(Array.from(ids));
    }

    public async fetchIds(
        ids: readonly number[],
        fetchOne: (id: number) => Promise<T>,
        rowsById: ReadonlyMap<number, SuggestionRowRef>
    ): Promise<void> {
        await Promise.all(
            ids.map(
                (id: number): Promise<void> =>
                    this.start(id, fetchOne, rowsById.get(id)).then(
                        (): void => undefined,
                        (): void => undefined
                    )
            )
        );
    }

    public snapshot(): { [id: number]: T } {
        const suggestions: { [id: number]: T } = {};
        this.entries.forEach((entry, id): void => {
            suggestions[id] = entry.value;
        });
        return suggestions;
    }

    private start(
        id: number,
        fetchOne: (id: number) => Promise<T>,
        row?: SuggestionRowRef
    ): Promise<T> {
        const existing = this.inflight.get(id);
        if (existing) {
            return existing;
        }
        const gen = this.generation.get(id) ?? 0;
        const promise = Promise.resolve()
            .then((): Promise<T> => fetchOne(id))
            .then((value: T): T => {
                if ((this.generation.get(id) ?? 0) === gen) {
                    this.entries.set(id, this.entryFrom(value, true, row));
                }
                return value;
            })
            .finally((): void => {
                if (this.inflight.get(id) === promise) {
                    this.inflight.delete(id);
                }
                const meta = this.inflightMeta.get(id);
                if (meta && meta.promise === promise) {
                    this.inflightMeta.delete(id);
                }
            });
        this.inflight.set(id, promise);
        this.inflightMeta.set(id, {
            promise,
            entityType: row && row.entityType,
            parentSourceId: row && row.parentSourceId,
        });
        return promise;
    }

    private entryFrom(
        value: T,
        fromNetwork: boolean,
        row?: SuggestionRowRef
    ): SuggestionCacheEntry<T> {
        return {
            value,
            fromNetwork,
            entityType: row && row.entityType,
            parentSourceId: row && row.parentSourceId,
        };
    }

    private bump(id: number): void {
        this.generation.set(id, (this.generation.get(id) ?? 0) + 1);
    }
}

// Applies the plan for the current page. Returns true when the cache changed.
export async function fillSuggestionCache<T>(
    cache: SuggestionCache<T>,
    rows: readonly SuggestionRowRef[],
    fetchOne: (id: number) => Promise<T>,
    empty: T
): Promise<boolean> {
    const plan = cache.planFor(rows);
    if (plan.length === 0) {
        return false;
    }
    const byId = new Map<number, SuggestionRowRef>();
    for (const row of rows) {
        byId.set(row.id, row);
    }
    const fetchIds: number[] = [];
    for (const step of plan) {
        if (step.action === 'seed-empty') {
            cache.seed(step.id, empty, byId.get(step.id));
        } else {
            fetchIds.push(step.id);
        }
    }
    if (fetchIds.length > 0) {
        await cache.fetchIds(fetchIds, fetchOne, byId);
    }
    return true;
}
